import {
  CODE_SESSION_WS_EVENTS,
  CODE_SESSIONS_WS_NAMESPACE,
} from "@frontend-shared/constants";
import type {
  ICodeSessionLiveEvent,
  ICodeSessionReplayMessage,
  ISubscribeSessionPayload,
} from "@frontend-shared/types";
import type { Socket } from "socket.io-client";
import { io } from "socket.io-client";

import { getLanguage, getProjectUuid, getToken } from "./auth";
import { getE2eMockSocketServerUrl } from "./websocket-e2e-hooks";

interface ISocketAuth {
  token: string;
  projectUuid: string | null;
  language: string | null;
  timezone: string;
}

type TEventHandler = (message: ICodeSessionLiveEvent) => void;
type TReplayHandler = (message: ICodeSessionReplayMessage) => void;
type TConnectionHandler = () => void;

// NOTE: Realtime client for the /api/code-sessions namespace. Deliberately
// lighter than lib/websocket.ts (chat): the gateway replays missed events on
// (re)subscribe, so we don't need background tracking or pending buffers —
// reconnecting and re-subscribing with the last seen sequence is enough.
// Handlers live in Sets wired once per socket, so they survive reconnects.
class CodeSessionsWebSocketService {
  private socket: Socket | null = null;
  private readonly eventHandlers = new Set<TEventHandler>();
  private readonly replayHandlers = new Set<TReplayHandler>();
  private readonly connectHandlers = new Set<TConnectionHandler>();
  private readonly disconnectHandlers = new Set<TConnectionHandler>();

  connect(): void {
    if (this.socket) {
      if (!this.socket.connected) {
        this.socket.connect();
      }
      return;
    }

    const auth = this.buildAuth();
    if (!auth) {
      throw new Error("No authentication token found");
    }

    // NOTE: E2E injects a mock Socket.IO URL that hardcodes the /api/chat
    // namespace, so reuse only its origin and append our namespace. In prod
    // the relative namespace path lets the Vite proxy forward to the backend.
    const mockUrl = getE2eMockSocketServerUrl();
    const url = mockUrl
      ? `${new URL(mockUrl).origin}${CODE_SESSIONS_WS_NAMESPACE}`
      : CODE_SESSIONS_WS_NAMESPACE;

    this.socket = io(url, {
      auth,
      transports: ["websocket"],
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 10000,
    });

    this.socket.on("connect", () => {
      this.connectHandlers.forEach((handler) => handler());
    });
    this.socket.on("disconnect", () => {
      this.disconnectHandlers.forEach((handler) => handler());
    });
    this.socket.on(
      CODE_SESSION_WS_EVENTS.event,
      (message: ICodeSessionLiveEvent) => {
        this.eventHandlers.forEach((handler) => handler(message));
      },
    );
    this.socket.on(
      CODE_SESSION_WS_EVENTS.replay,
      (message: ICodeSessionReplayMessage) => {
        this.replayHandlers.forEach((handler) => handler(message));
      },
    );
  }

  // NOTE: socket.io re-reads `auth` on every (re)connection attempt, so the
  // object is rebuilt from the current token rather than captured at connect().
  private buildAuth(): ISocketAuth | null {
    const token = getToken();
    if (!token) {
      return null;
    }

    return {
      token: `Bearer ${token}`,
      projectUuid: getProjectUuid(),
      language: getLanguage(),
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC",
    };
  }

  // NOTE: Called from apiClient.onTokenRefresh (api/services.ts). Without it the
  // handshake keeps the token captured at connect() time, and the first
  // reconnect after the access token rotates is refused — a code session can run
  // far longer than JWT_ACCESS_EXPIRES_IN, so that is a live scenario.
  //
  // Unlike the chat socket this does not defer the reconnect around in-flight
  // work: the gateway replays every event after `lastSequence` on re-subscribe,
  // so cycling the handshake costs a round trip and loses nothing.
  updateAuthAndReconnect(): void {
    const auth = this.buildAuth();
    if (!auth || !this.socket) {
      return;
    }

    this.socket.auth = auth;

    // NOTE: connect() is a no-op on an already-connected socket, so the fresh
    // credentials only reach the server after an explicit disconnect.
    if (this.socket.connected) {
      this.socket.disconnect();
    }
    this.socket.connect();
  }

  isConnected(): boolean {
    return this.socket?.connected ?? false;
  }

  subscribeSession(payload: ISubscribeSessionPayload): void {
    this.socket?.emit(CODE_SESSION_WS_EVENTS.subscribeSession, payload);
  }

  unsubscribeSession(sessionUuid: string): void {
    this.socket?.emit(CODE_SESSION_WS_EVENTS.unsubscribeSession, {
      sessionUuid,
    });
  }

  onEvent(handler: TEventHandler): () => void {
    this.eventHandlers.add(handler);
    return () => {
      this.eventHandlers.delete(handler);
    };
  }

  onReplay(handler: TReplayHandler): () => void {
    this.replayHandlers.add(handler);
    return () => {
      this.replayHandlers.delete(handler);
    };
  }

  onConnect(handler: TConnectionHandler): () => void {
    this.connectHandlers.add(handler);
    return () => {
      this.connectHandlers.delete(handler);
    };
  }

  onDisconnect(handler: TConnectionHandler): () => void {
    this.disconnectHandlers.add(handler);
    return () => {
      this.disconnectHandlers.delete(handler);
    };
  }

  disconnect(): void {
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
    }
  }
}

export const codeSessionsWebSocketService = new CodeSessionsWebSocketService();

// NOTE: Mirrors lib/websocket.ts — exposes the auth-reconnect trigger to
// Playwright when the E2E hook object is present (set via addInitScript).
// Guarded so production builds, where `__NSAI_E2E_WEBSOCKET__` is undefined,
// never attach it.
if (
  typeof globalThis.window !== "undefined" &&
  globalThis.window.__NSAI_E2E_WEBSOCKET__
) {
  globalThis.window.__NSAI_E2E_WEBSOCKET__.triggerCodeSessionsAuthReconnect =
    (): void => codeSessionsWebSocketService.updateAuthAndReconnect();
}
